import React, { useContext, useState } from 'react';
import { App, Button, Col, Flex, Form, Grid, Input, Modal, Popover, Row, Space, Typography } from 'antd';
import { AxiosError, HttpStatusCode } from 'axios';
import { QuestionCircleTwoTone } from '@ant-design/icons';
import { useForm } from 'antd/lib/form/Form';
import { useMutation } from '@tanstack/react-query';

import { AreaRestritaUsuarioService, CredenciaisDeLogin, LoginComUsuario, PayloadDeRecuperacaoDeSenha } from '../../../Services/Domain/AreaRestrita/UsuarioService';
import { LOCAL_STORAGE_JWT_KEY_AREA_RESTRITA } from '../../../Constants';
import { SessaoARContext, TypeSessaoContext } from '../../Contexts/SessaoContext';
import { configurarSessaoDoAxios } from '../../../Services/Axios';
import { Link, useNavigate } from 'react-router-dom';
import { CancelarSessaoERemoverToken } from '../Sistema/Splash';
import { PainelDePrimeiroAcessoDaEscola } from './Acesso.primeiroAcesso';
import { TypeSessaoDaAreaRestrita } from '../../../Models/Domain/AreaRestrita/Sessao';
import { notificarErroNaRequisicao, PropriedadesDeNotificacao } from '../../../Utils/Notificacoes';
import { PainelDeRecuperacaoDeSenha } from './Acesso.recuperarSenha';
import { ehEmail } from '../../../Utils/Formatacao';

export const ConfigurarSessaoDoUsuario = (dadosDaSessao: TypeSessaoDaAreaRestrita, setSessao?: TypeSessaoContext['setSessao'] ) => {
    const token = dadosDaSessao.token;
    if (token == undefined) return;

    localStorage.setItem(LOCAL_STORAGE_JWT_KEY_AREA_RESTRITA, token);

    configurarSessaoDoAxios(token, () => CancelarSessaoERemoverToken(setSessao));
    setSessao?.(dadosDaSessao);
}

export const PainelLogin: React.FC<{
    hrefCadastro?: string
}> = (props) => {

    const { notification } = App.useApp();
    const breakpoints = Grid.useBreakpoint();

    const SessaoAR = useContext(SessaoARContext);
    const navigate = useNavigate();

    interface IFormularioDeLogin {
        acesso: string,
        senha: string
    }

    const [statusDaModalDePrimeiraSenhaDaEscola, setStatusDaModalDePrimeiraSenhaDaEscola] = useState<{ exibir: boolean, login?: Required<LoginComUsuario> }>({ exibir: false })

    const [exibirModalDeRecuperacaoDeSenha, setExibirModalDeRecuperacaoDeSenha] = useState<boolean>(false);

    const mutationLogin = useMutation({
        mutationFn: (valores: IFormularioDeLogin) => {
            let credenciais: CredenciaisDeLogin;

            const acessandoComEmail = ehEmail(valores.acesso);
            if(acessandoComEmail){
                credenciais = {
                    email: valores.acesso,
                    senha: valores.senha
                }
            } else {
                credenciais = {
                    usuario: valores.acesso,
                    senha: valores.senha
                }
            }

            return new AreaRestritaUsuarioService().logar(credenciais);
        },
        onSuccess: (data) => {
            ConfigurarSessaoDoUsuario(data.data, SessaoAR?.setSessao);
            navigate(localStorage.getItem("Rota_Get_AreaRestrita") ?? '/viagens');
        },

        onError: (error: AxiosError, valores) => {
            type RequerValidacao = {
                alterarSenha: boolean
            }
            if (error.response?.status === HttpStatusCode.Unauthorized) {
                notification.error({ message: "Usuario ou senha inválidos.", placement: "top" });
            }
            else if (
                error.response?.status === HttpStatusCode.PreconditionRequired && 
                ((error.response.data as any)?.data as RequerValidacao)?.alterarSenha == true &&
                valores.acesso != null && valores.senha != null
            ){
                notification.info({ message: "É necessário definir sua primeira senha", placement: "top" });
                setStatusDaModalDePrimeiraSenhaDaEscola({exibir: true, login: {senha: valores.senha, usuario: valores.acesso}})
            }
            else {
                notificarErroNaRequisicao(error)
            }
        }
    });

    return (
        <>
            <Modal 
                title={"Esqueceu sua senha"}
                open={exibirModalDeRecuperacaoDeSenha}
                destroyOnClose
                onCancel={() => {
                    setExibirModalDeRecuperacaoDeSenha(false);
                }}
                footer={false}
            >
                <PainelDeRecuperacaoDeSenha depoisDeFinalizar={() => {
                    setExibirModalDeRecuperacaoDeSenha(false)
                }}/>

            </Modal>

            <Modal
                title={"Primeira senha"}
                destroyOnClose
                open={statusDaModalDePrimeiraSenhaDaEscola.exibir == true}
                width={breakpoints.xl? "40vw": "80vw"}
                maskClosable={false}
                onCancel={() => {
                    setStatusDaModalDePrimeiraSenhaDaEscola({exibir: false})
                }}
                onOk={() => { }}
                footer={null}
            >
                {statusDaModalDePrimeiraSenhaDaEscola.exibir == true ? 
                    <PainelDePrimeiroAcessoDaEscola 
                        credenciais={{
                            usuario: statusDaModalDePrimeiraSenhaDaEscola.login?.usuario!,
                            senha: statusDaModalDePrimeiraSenhaDaEscola.login?.senha!,
                        }} 
                    /> 
                    :
                    undefined
                }
            </Modal>

            <Form<IFormularioDeLogin>
                name="login"
                onFinish={(valores) => mutationLogin.mutate(valores)}
                autoComplete="on"
                layout="vertical"
                style={{ minWidth: breakpoints.xs ? '280px' : '380px', flexGrow: 1 }}
            >
            <Typography>
                <Typography.Title level={4} style={{marginTop: 0, textAlign: "center"}}> Quiron Turismo </Typography.Title>
                <Typography.Paragraph style={{marginTop: 0, textAlign: "center"}}>Faça seu login</Typography.Paragraph>
            </Typography>
                <Form.Item<IFormularioDeLogin>
                    label={<><Space size={"small"}> E-mail  <Popover title={<>
                        Identificador de Acesso
                        <p>Responsavel: Utilizar e-mail como identificador de acesso</p>
                        <p>Escola: Utilizar CNPJ como identificador de acesso</p>
                    </>}> <QuestionCircleTwoTone /> </Popover> </Space> </>}
                    name={["acesso"]}
                    rules={[{ required: true, message: "Informe seu E-mail." }]}
                >
                    <Input/>
                </Form.Item>
                <Form.Item<IFormularioDeLogin>
                    label="Senha"
                    name={["senha"]}
                    rules={[{ required: true, message: "Informe sua senha." }]}
                >
                    <Input.Password/>
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                    <Flex vertical gap={8} align='center'>
                        <Button type="primary" htmlType="submit" loading={mutationLogin.isPending} block>Entrar</Button>

                        <Button type="link" onClick={() => {
                            setExibirModalDeRecuperacaoDeSenha(true);
                        }}> Esqueceu sua senha? </Button>

                        {/* {props.hrefCadastro && <Link to={props.hrefCadastro}>Primeiro acesso? Faça seu cadastro.</Link>} */}
                    </Flex>
                </Form.Item>

            </Form>
        </>
    )
}
